import * as React from "react";

/**
 * Pending connection state passed from ReactFlowCanvas → DecisionTreeEdit.
 * Contains the source/target node IDs and screen position for the popover.
 */
export interface PendingConnection {
    sourceNodeId: string;
    targetNodeId: string;
    /** Screen position (clientX, clientY) where the popover should appear */
    position: { x: number; y: number };
}

export interface ConnectionAnswerPopoverProps {
    /** The pending connection to confirm */
    connection: PendingConnection;
    /** Called with the answer text when user confirms */
    onConfirm: (answerText: string) => void;
    /** Called when user cancels */
    onCancel: () => void;
    /** Optional initial value for the input (used for edit mode) */
    initialValue?: string;
    /** Optional title override (default: "🔗 New Connection") */
    title?: string;
    /** Optional description override */
    description?: string;
    /** Optional confirm button label (default: "Connect") */
    confirmLabel?: string;
}

/**
 * Inline popover for entering or editing answer text on edges/links.
 *
 * Used for:
 * - New connections (drag-to-link): empty input, "Connect" button
 * - Edit answer (right-click edge): pre-filled with current answer, "Save" button
 *
 * Positioned at the specified location. Auto-focuses the input.
 * Supports Enter to confirm, Escape to cancel.
 */
export const ConnectionAnswerPopover: React.FC<ConnectionAnswerPopoverProps> = ({
    connection,
    onConfirm,
    onCancel,
    initialValue = "",
    title = "🔗 New Connection",
    description = "Enter the answer/label for this link:",
    confirmLabel = "Connect"
}) => {
    const [answerText, setAnswerText] = React.useState(initialValue);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Auto-focus the input on mount
    React.useEffect(() => {
        // Small delay to ensure the popover is rendered
        const timer = setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    // Close on Escape, submit on Enter
    const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
        } else if (e.key === "Enter" && answerText.trim()) {
            e.preventDefault();
            onConfirm(answerText.trim());
        }
    }, [answerText, onConfirm, onCancel]);

    // Close on outside click
    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onCancel();
            }
        };
        // Delay listener to avoid catching the connection-drop click itself
        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onCancel]);

    // Adjust position to stay within viewport
    const popoverWidth = 260;
    const popoverHeight = 120;
    const adjustedX = Math.min(connection.position.x, window.innerWidth - popoverWidth - 16);
    const adjustedY = Math.min(connection.position.y, window.innerHeight - popoverHeight - 16);

    return (
        <div
            ref={containerRef}
            style={{
                position: "fixed",
                left: Math.max(8, adjustedX),
                top: Math.max(8, adjustedY),
                zIndex: 1000,
                background: "white",
                borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                padding: "12px 14px",
                width: popoverWidth,
                border: "1px solid #d5dbdb"
            }}
        >
            <div style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#16191f",
                marginBottom: "8px"
            }}>
                {title}
            </div>
            <div style={{
                fontSize: "12px",
                color: "#687078",
                marginBottom: "8px"
            }}>
                {description}
            </div>
            <input
                ref={inputRef}
                type="text"
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Yes, No, Option A..."
                style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: "13px",
                    border: "1px solid #aab7b8",
                    borderRadius: "4px",
                    outline: "none",
                    boxSizing: "border-box",
                    marginBottom: "8px"
                }}
            />
            <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "6px"
            }}>
                <button
                    onClick={onCancel}
                    style={{
                        padding: "4px 12px",
                        fontSize: "12px",
                        border: "1px solid #aab7b8",
                        borderRadius: "4px",
                        background: "white",
                        cursor: "pointer",
                        color: "#16191f"
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={() => answerText.trim() && onConfirm(answerText.trim())}
                    disabled={!answerText.trim()}
                    style={{
                        padding: "4px 12px",
                        fontSize: "12px",
                        border: "1px solid #0972d3",
                        borderRadius: "4px",
                        background: answerText.trim() ? "#0972d3" : "#d5dbdb",
                        color: "white",
                        cursor: answerText.trim() ? "pointer" : "not-allowed",
                        fontWeight: 600
                    }}
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    );
};
